export interface DocArticle {
  slug: string;
  categoryKey: string;
  title: { en: string; pt: string };
  content: { en: string; pt: string };
}

export const documentationCategories = [
  { key: 'getting-started', title: { en: 'Getting Started', pt: 'Primeiros Passos' } },
  { key: 'dashboards', title: { en: 'Dashboards & Analytics', pt: 'Dashboards & Analytics' } },
  { key: 'integrations', title: { en: 'Integrations', pt: 'Integrações' } },
  { key: 'settings', title: { en: 'Settings & Admin', pt: 'Configurações & Admin' } },
];

export const documentationArticles: DocArticle[] = [
  // ─── GETTING STARTED ───────────────────────────────────────
  {
    slug: 'creating-account',
    categoryKey: 'getting-started',
    title: { en: 'Creating your account and organization', pt: 'Criando sua conta e organização' },
    content: {
      en: `## Creating Your Account

### 1. Sign Up

Visit [revops.digitais.ai/signup](/signup) and fill in your details:

- **Full name** — Your display name within the platform.
- **Work email** — Use your company email. A verification link will be sent.
- **Password** — Minimum 8 characters.

After submitting, check your inbox and click the confirmation link.

### 2. Create Your Organization

Once your email is verified and you sign in for the first time, you'll be guided through creating your organization:

1. **Organization name** — The name of your company (e.g., "Acme Corp").
2. **Default language** — Choose between Portuguese (Brazil) or English. This sets the default language for all members.

> **Tip:** The organization name can be changed later in **Settings → General**.

### 3. Invite Your Team

After creating the organization, you can invite team members:

- Go to **Settings → Users**.
- Click **Invite User** and enter the colleague's email.
- Choose a **permission profile** to control what they can access.

Each invited member receives an email with a link to join your organization. They create their own account and are automatically associated with your org.

### 4. Next Steps

Once your organization is set up:

- [Connect your CRM](/documentation/connecting-crm) to start syncing data.
- [Map your pipeline stages](/documentation/mapping-pipeline) to configure your funnel.
- [Set targets and goals](/documentation/setting-targets) to enable performance tracking.

---

### FAQ

**Can I belong to multiple organizations?**
Yes. If you're invited to another organization, you'll be able to switch between them after login.

**Can I change the organization language later?**
Yes. Go to **Settings → General** and update the default language. Individual users can also set their own preferred language in their profile.`,

      pt: `## Criando Sua Conta

### 1. Cadastro

Acesse [revops.digitais.ai/signup](/signup) e preencha seus dados:

- **Nome completo** — Seu nome de exibição na plataforma.
- **E-mail corporativo** — Use o e-mail da sua empresa. Um link de verificação será enviado.
- **Senha** — Mínimo de 8 caracteres.

Após enviar, verifique sua caixa de entrada e clique no link de confirmação.

### 2. Criar Sua Organização

Ao verificar o e-mail e fazer login pela primeira vez, você será guiado para criar sua organização:

1. **Nome da organização** — O nome da sua empresa (ex: "Acme Corp").
2. **Idioma padrão** — Escolha entre Português (Brasil) ou Inglês. Isso define o idioma padrão para todos os membros.

> **Dica:** O nome da organização pode ser alterado depois em **Configurações → Geral**.

### 3. Convidar Sua Equipe

Após criar a organização, você pode convidar membros:

- Acesse **Configurações → Usuários**.
- Clique em **Convidar Usuário** e insira o e-mail do colega.
- Escolha um **perfil de permissão** para controlar o que ele pode acessar.

Cada membro convidado recebe um e-mail com um link para entrar na sua organização. Eles criam a própria conta e são associados automaticamente à sua org.

### 4. Próximos Passos

Após configurar sua organização:

- [Conecte seu CRM](/documentation/connecting-crm) para começar a sincronizar dados.
- [Mapeie os estágios do pipeline](/documentation/mapping-pipeline) para configurar seu funil.
- [Configure metas e objetivos](/documentation/setting-targets) para ativar o acompanhamento de performance.

---

### FAQ

**Posso pertencer a múltiplas organizações?**
Sim. Se for convidado para outra organização, poderá alternar entre elas após o login.

**Posso mudar o idioma da organização depois?**
Sim. Vá em **Configurações → Geral** e atualize o idioma padrão. Cada usuário também pode definir seu próprio idioma preferido no perfil.`
    }
  },
  {
    slug: 'connecting-crm',
    categoryKey: 'getting-started',
    title: { en: 'Connecting your CRM', pt: 'Conectando seu CRM' },
    content: {
      en: `## Connecting Your CRM

RevOps AI integrates natively with **HubSpot** and **RD Station CRM**. The connection process takes about 2 minutes.

### Supported CRMs

| CRM | Connection Method | Status |
|-----|-------------------|--------|
| HubSpot | OAuth 2.0 | ✅ Available |
| RD Station CRM | API Token | ✅ Available |

### Connecting HubSpot

1. Go to **Settings → Integrations**.
2. Click **Connect** on the HubSpot card.
3. You'll be redirected to HubSpot to authorize access.
4. Select the HubSpot account you want to connect and click **Allow**.
5. You'll be redirected back to RevOps AI with the connection active.

> **Important:** You need admin access to the HubSpot account to authorize the integration.

#### What data is synced from HubSpot?

- **Deals** — All deals with amount, stage, pipeline, owner, close date, and custom fields.
- **Contacts** — Contact information associated with deals.
- **Companies** — Company data linked to deals.
- **Activities** — Emails, calls, meetings, and notes linked to deals.
- **Products** — Product line items associated with deals.
- **Tasks** — Tasks associated with deals and contacts.

### Connecting RD Station CRM

1. Go to **Settings → Integrations**.
2. Click **Connect** on the RD Station CRM card.
3. Enter your **API Token** (found in RD Station CRM → Settings → Integrations → Token).
4. Click **Save and Connect**.

#### What data is synced from RD Station?

- **Deals** — All deals with value, stage, pipeline, and owner.
- **Contacts** — Contact information linked to deals.
- **Organizations** — Company data.
- **Activities** — Interaction history.
- **Tasks** — Pending and completed tasks.

### After Connecting

Once connected, the initial sync begins automatically. Depending on the volume of data, this may take from a few minutes to about 30 minutes.

You can monitor sync progress in **Settings → Integrations** where you'll see:
- Sync status (running, completed, failed)
- Number of records synced
- Last sync timestamp

### Next Steps

- [Map your CRM fields](/documentation/field-mapping) to ensure data is correctly interpreted.
- [Configure your pipeline stages](/documentation/mapping-pipeline) in the funnel builder.

---

### FAQ

**Can I connect multiple CRMs?**
Currently, each organization supports one CRM connection at a time.

**Is my CRM data safe?**
Yes. We use encrypted connections (TLS) and never modify your CRM data. RevOps AI has read-only access.

**How often is data synced?**
Data is synced automatically once per day. You can also trigger manual syncs from the Integrations page.`,

      pt: `## Conectando Seu CRM

O RevOps AI integra nativamente com **HubSpot** e **RD Station CRM**. O processo de conexão leva cerca de 2 minutos.

### CRMs Suportados

| CRM | Método de Conexão | Status |
|-----|-------------------|--------|
| HubSpot | OAuth 2.0 | ✅ Disponível |
| RD Station CRM | Token de API | ✅ Disponível |

### Conectando o HubSpot

1. Acesse **Configurações → Integrações**.
2. Clique em **Conectar** no card do HubSpot.
3. Você será redirecionado ao HubSpot para autorizar o acesso.
4. Selecione a conta do HubSpot que deseja conectar e clique em **Permitir**.
5. Você será redirecionado de volta ao RevOps AI com a conexão ativa.

> **Importante:** Você precisa de acesso de administrador na conta do HubSpot para autorizar a integração.

#### Quais dados são sincronizados do HubSpot?

- **Deals** — Todos os deals com valor, estágio, pipeline, responsável, data de fechamento e campos customizados.
- **Contatos** — Informações de contato associadas aos deals.
- **Empresas** — Dados da empresa vinculados aos deals.
- **Atividades** — E-mails, ligações, reuniões e notas vinculadas aos deals.
- **Produtos** — Itens de produto associados aos deals.
- **Tarefas** — Tarefas associadas a deals e contatos.

### Conectando o RD Station CRM

1. Acesse **Configurações → Integrações**.
2. Clique em **Conectar** no card do RD Station CRM.
3. Insira seu **Token de API** (encontrado em RD Station CRM → Configurações → Integrações → Token).
4. Clique em **Salvar e Conectar**.

#### Quais dados são sincronizados do RD Station?

- **Deals** — Todos os deals com valor, estágio, pipeline e responsável.
- **Contatos** — Informações de contato vinculadas aos deals.
- **Organizações** — Dados de empresa.
- **Atividades** — Histórico de interações.
- **Tarefas** — Tarefas pendentes e concluídas.

### Após Conectar

Uma vez conectado, a sincronização inicial começa automaticamente. Dependendo do volume de dados, pode levar de alguns minutos a cerca de 30 minutos.

Você pode acompanhar o progresso da sincronização em **Configurações → Integrações** onde verá:
- Status da sincronização (em andamento, concluída, falha)
- Número de registros sincronizados
- Timestamp da última sincronização

### Próximos Passos

- [Mapeie seus campos do CRM](/documentation/field-mapping) para garantir a correta interpretação dos dados.
- [Configure os estágios do pipeline](/documentation/mapping-pipeline) no construtor de funil.

---

### FAQ

**Posso conectar múltiplos CRMs?**
Atualmente, cada organização suporta uma conexão de CRM por vez.

**Meus dados do CRM estão seguros?**
Sim. Usamos conexões criptografadas (TLS) e nunca modificamos seus dados no CRM. O RevOps AI tem acesso somente leitura.

**Com que frequência os dados são sincronizados?**
Os dados são sincronizados automaticamente uma vez por dia. Você também pode disparar sincronizações manuais pela página de Integrações.`
    }
  },
  {
    slug: 'mapping-pipeline',
    categoryKey: 'getting-started',
    title: { en: 'Mapping pipeline stages', pt: 'Mapeando estágios do pipeline' },
    content: {
      en: `## Mapping Pipeline Stages

Pipeline mapping is essential for RevOps AI to understand your sales process and generate accurate analytics.

### What is Pipeline Mapping?

When you connect your CRM, RevOps AI imports your deal pipelines and stages. You then need to:

1. **Classify each stage** into a category (Open, Won, or Lost).
2. **Order the stages** to reflect the real progression of your funnel.
3. **Optionally rename stages** with aliases for clearer reporting.

### How to Configure

1. Go to **Settings → Funnels**.
2. You'll see all pipelines imported from your CRM.
3. For each pipeline, click **Configure**.

#### Stage Categories

Each stage must be assigned to one of these categories:

| Category | Description | Example Stages |
|----------|-------------|----------------|
| **Open** | Active deal in progress | Qualification, Proposal, Negotiation |
| **Won** | Successfully closed deal | Closed Won, Contract Signed |
| **Lost** | Deal that didn't close | Closed Lost, Disqualified |

#### Stage Order

Drag and drop stages to arrange them in the order deals typically progress through your funnel. This order is used for:

- Funnel visualization
- Conversion rate calculations between stages
- Stage velocity metrics

#### Win/Lost Detection Mode

For each pipeline, choose how RevOps AI detects won and lost deals:

- **By stage** — Uses the stage category (recommended).
- **By deal status** — Uses the deal's status field from the CRM.

### Multi-Pipeline Support

If you have multiple pipelines (e.g., "New Business" and "Renewals"), you can configure each independently. You can also:

- Set **pipeline types** (e.g., New Business, Expansion, Renewal).
- Configure **handoff connections** between pipelines.
- Define which pipelines are **active** for analytics.

### Best Practices

- **Keep it simple** — Start with your main sales pipeline. Add others later.
- **Be precise with categories** — Miscategorized stages lead to incorrect metrics.
- **Review periodically** — When your sales process changes, update your pipeline configuration.

### Next Steps

- [Set targets and goals](/documentation/setting-targets) based on your pipeline configuration.
- [View your Executive Overview](/documentation/executive-overview) to see pipeline analytics.`,

      pt: `## Mapeando Estágios do Pipeline

O mapeamento de pipeline é essencial para que o RevOps AI entenda seu processo comercial e gere análises precisas.

### O que é o Mapeamento de Pipeline?

Quando você conecta seu CRM, o RevOps AI importa seus pipelines e estágios de deals. Então você precisa:

1. **Classificar cada estágio** em uma categoria (Aberto, Ganho ou Perdido).
2. **Ordenar os estágios** para refletir a progressão real do seu funil.
3. **Opcionalmente renomear estágios** com aliases para relatórios mais claros.

### Como Configurar

1. Acesse **Configurações → Funis**.
2. Você verá todos os pipelines importados do seu CRM.
3. Para cada pipeline, clique em **Configurar**.

#### Categorias de Estágio

Cada estágio deve ser associado a uma destas categorias:

| Categoria | Descrição | Exemplos de Estágios |
|-----------|-----------|---------------------|
| **Aberto** | Deal ativo em andamento | Qualificação, Proposta, Negociação |
| **Ganho** | Deal fechado com sucesso | Fechado Ganho, Contrato Assinado |
| **Perdido** | Deal que não fechou | Fechado Perdido, Desqualificado |

#### Ordem dos Estágios

Arraste e solte os estágios para organizá-los na ordem em que os deals normalmente progridem pelo seu funil. Essa ordem é usada para:

- Visualização do funil
- Cálculos de taxa de conversão entre estágios
- Métricas de velocidade por estágio

#### Modo de Detecção de Ganho/Perda

Para cada pipeline, escolha como o RevOps AI detecta deals ganhos e perdidos:

- **Por estágio** — Usa a categoria do estágio (recomendado).
- **Por status do deal** — Usa o campo de status do deal no CRM.

### Suporte a Múltiplos Pipelines

Se você tem múltiplos pipelines (ex: "Novos Negócios" e "Renovações"), pode configurar cada um independentemente. Também é possível:

- Definir **tipos de pipeline** (ex: Novo Negócio, Expansão, Renovação).
- Configurar **conexões de handoff** entre pipelines.
- Definir quais pipelines estão **ativos** para analytics.

### Melhores Práticas

- **Mantenha simples** — Comece pelo pipeline principal de vendas. Adicione outros depois.
- **Seja preciso com as categorias** — Estágios mal categorizados levam a métricas incorretas.
- **Revise periodicamente** — Quando seu processo comercial mudar, atualize a configuração do pipeline.

### Próximos Passos

- [Configure metas e objetivos](/documentation/setting-targets) com base na configuração do seu pipeline.
- [Veja sua Visão Executiva](/documentation/executive-overview) para visualizar as análises do pipeline.`
    }
  },
  {
    slug: 'setting-targets',
    categoryKey: 'getting-started',
    title: { en: 'Setting targets and goals', pt: 'Configurando metas e objetivos' },
    content: {
      en: `## Setting Targets and Goals

Targets allow RevOps AI to measure performance against your plan, enabling features like goal attainment tracking, pipeline coverage analysis, and forecast accuracy.

### How to Set Targets

1. Go to **Targets** in the main navigation.
2. Select the **month** you want to configure.
3. Enter the revenue target for each sales rep or for the team as a whole.

### Target Types

| Type | Description |
|------|-------------|
| **Revenue Target** | Total revenue expected to be closed in the period |
| **Per-Rep Target** | Individual targets assigned to each salesperson |

### How Targets Are Used

Once set, targets power several features:

- **Executive Overview** — Shows target attainment percentage and gap to goal.
- **Sales Burndown** — Visualizes daily progress toward the target throughout the month.
- **Pipeline Coverage** — Calculates how much pipeline you need relative to your target.
- **Forecast** — Compares mathematical forecast against targets.

### Best Practices

- **Set targets monthly** — Update targets at the beginning of each month.
- **Be realistic** — Base targets on historical data and pipeline health.
- **Include all reps** — Ensure every active salesperson has an individual target for accurate analytics.
- **Review mid-month** — Use the burndown chart to assess if targets need adjustment.

### Next Steps

- [Explore the Executive Overview](/documentation/executive-overview) to see target attainment.
- [Use the Forecast module](/documentation/forecast-module) to see mathematical predictions vs. targets.`,

      pt: `## Configurando Metas e Objetivos

As metas permitem que o RevOps AI meça a performance em relação ao seu planejamento, habilitando funcionalidades como acompanhamento de atingimento, análise de cobertura de pipeline e precisão de forecast.

### Como Definir Metas

1. Acesse **Metas** na navegação principal.
2. Selecione o **mês** que deseja configurar.
3. Insira a meta de receita para cada vendedor ou para a equipe como um todo.

### Tipos de Meta

| Tipo | Descrição |
|------|-----------|
| **Meta de Receita** | Receita total esperada para ser fechada no período |
| **Meta por Vendedor** | Metas individuais atribuídas a cada vendedor |

### Como as Metas São Usadas

Uma vez definidas, as metas alimentam diversas funcionalidades:

- **Visão Executiva** — Mostra o percentual de atingimento e o gap para a meta.
- **Sales Burndown** — Visualiza o progresso diário em direção à meta ao longo do mês.
- **Cobertura de Pipeline** — Calcula quanto pipeline você precisa em relação à sua meta.
- **Forecast** — Compara o forecast matemático contra as metas.

### Melhores Práticas

- **Defina metas mensalmente** — Atualize as metas no início de cada mês.
- **Seja realista** — Baseie as metas em dados históricos e saúde do pipeline.
- **Inclua todos os vendedores** — Garanta que cada vendedor ativo tenha uma meta individual para análises precisas.
- **Revise no meio do mês** — Use o gráfico de burndown para avaliar se as metas precisam de ajuste.

### Próximos Passos

- [Explore a Visão Executiva](/documentation/executive-overview) para ver o atingimento de metas.
- [Use o módulo de Forecast](/documentation/forecast-module) para ver previsões matemáticas vs. metas.`
    }
  },

  // ─── DASHBOARDS & ANALYTICS ────────────────────────────────
  {
    slug: 'executive-overview',
    categoryKey: 'dashboards',
    title: { en: 'Executive Overview guide', pt: 'Guia do Executive Overview' },
    content: {
      en: `## Executive Overview

The Executive Overview is your command center for revenue performance. It answers the question: **"How is my revenue operation performing right now?"**

### Key Metrics (KPI Cards)

At the top of the page, you'll find the most important KPIs:

| Metric | What It Measures | Why It Matters |
|--------|-----------------|----------------|
| **Closed Revenue** | Total won revenue in the period | Direct measure of sales success |
| **Target Attainment** | % of target achieved | Shows if you're on track |
| **Win Rate** | Won ÷ (Won + Lost) | Sales effectiveness indicator |
| **Average Deal Size** | Closed revenue ÷ number of won deals | Revenue quality metric |
| **Sales Cycle** | Average days from creation to close | Speed of your sales process |
| **Pipeline Value** | Sum of open deal values | Future revenue potential |

### Written Summary

Below the KPIs, an AI-generated summary provides a concise narrative of the current situation, highlighting:

- Performance trends compared to previous periods
- Key risks and opportunities
- Priority areas requiring attention

### Charts and Visualizations

The dashboard includes:

- **Revenue trend** — Monthly revenue progression with target comparison.
- **Pipeline evolution** — How pipeline value changes over time.
- **Conversion funnel** — Stage-by-stage conversion rates.
- **Rep performance** — Individual salesperson metrics.

### Date Filters

Use the date filters at the top to analyze different time periods. The dashboard automatically adjusts all metrics and charts to the selected range.

### Best Practices

- **Check daily** — Use it as your morning briefing.
- **Focus on trends** — Single data points matter less than direction.
- **Drill down** — Click on metrics to navigate to detailed analysis pages.

### Related Features

- [Revenue Metrics](/documentation/revenue-metrics) — Deep dive into revenue analytics.
- [Sales Burndown](/documentation/burndown-coverage) — Daily progress tracking.
- [Forecast](/documentation/forecast-module) — Mathematical revenue predictions.`,

      pt: `## Visão Executiva

A Visão Executiva é seu centro de comando para performance de receita. Ela responde à pergunta: **"Como está minha operação de receita agora?"**

### Métricas Principais (Cards de KPI)

No topo da página, você encontra os KPIs mais importantes:

| Métrica | O Que Mede | Por Que Importa |
|---------|-----------|----------------|
| **Receita Fechada** | Receita total ganha no período | Medida direta do sucesso em vendas |
| **Atingimento de Meta** | % da meta alcançada | Mostra se você está no caminho certo |
| **Win Rate** | Ganhos ÷ (Ganhos + Perdidos) | Indicador de efetividade comercial |
| **Ticket Médio** | Receita fechada ÷ número de deals ganhos | Métrica de qualidade da receita |
| **Ciclo de Vendas** | Dias médios da criação ao fechamento | Velocidade do seu processo comercial |
| **Valor do Pipeline** | Soma dos valores dos deals abertos | Potencial de receita futura |

### Resumo Escrito

Abaixo dos KPIs, um resumo gerado por IA fornece uma narrativa concisa da situação atual, destacando:

- Tendências de performance comparadas a períodos anteriores
- Riscos e oportunidades principais
- Áreas prioritárias que requerem atenção

### Gráficos e Visualizações

O dashboard inclui:

- **Tendência de receita** — Progressão mensal de receita com comparação à meta.
- **Evolução do pipeline** — Como o valor do pipeline muda ao longo do tempo.
- **Funil de conversão** — Taxas de conversão estágio a estágio.
- **Performance por vendedor** — Métricas individuais de cada vendedor.

### Filtros de Data

Use os filtros de data no topo para analisar diferentes períodos. O dashboard ajusta automaticamente todas as métricas e gráficos para o intervalo selecionado.

### Melhores Práticas

- **Consulte diariamente** — Use como seu briefing matinal.
- **Foque em tendências** — Pontos isolados importam menos que a direção.
- **Aprofunde** — Clique nas métricas para navegar a páginas de análise detalhada.

### Funcionalidades Relacionadas

- [Métricas de Receita](/documentation/revenue-metrics) — Análise aprofundada de receita.
- [Sales Burndown](/documentation/burndown-coverage) — Acompanhamento diário de progresso.
- [Forecast](/documentation/forecast-module) — Previsões matemáticas de receita.`
    }
  },
  {
    slug: 'revenue-metrics',
    categoryKey: 'dashboards',
    title: { en: 'Understanding revenue metrics', pt: 'Entendendo métricas de receita' },
    content: {
      en: `## Understanding Revenue Metrics

RevOps AI tracks key revenue metrics using consistent formulas across the entire platform.

### Core Revenue Metrics

#### Closed Revenue
- **Formula:** Sum of deal values for deals marked as Won in the selected period.
- **Use:** Primary measure of sales performance.

#### Win Rate
- **Formula:** Won Deals ÷ (Won Deals + Lost Deals) × 100
- **Use:** Measures sales effectiveness. Does not count open deals.

#### Average Deal Size (Ticket)
- **Formula:** Total Closed Revenue ÷ Number of Won Deals
- **Use:** Indicates revenue quality and deal composition.

#### Sales Cycle
- **Formula:** Average number of days between deal creation date and close date for won deals.
- **Use:** Measures sales velocity. Shorter cycles generally indicate a healthier process.

#### Pipeline Coverage
- **Formula:** Total Open Pipeline Value ÷ Revenue Target
- **Use:** Shows how much pipeline you have relative to your goal. A ratio of 3x or higher is typically healthy.

#### Sales Velocity
- **Formula:** (Number of Opportunities × Win Rate × Average Deal Size) ÷ Sales Cycle Length
- **Use:** Quantifies revenue generation speed. Improving any component improves velocity.

### How to Interpret

- **Revenue trending down?** → Check win rate and deal volume.
- **Win rate declining?** → Analyze lost deal reasons and stage conversion.
- **Long sales cycles?** → Look for bottleneck stages in the funnel.
- **Low pipeline coverage?** → Focus on lead generation and qualification.

### Data Consistency Guarantee

Every metric uses the same formula everywhere it appears — in the Executive Overview, Reports, Forecast, and AI Advisor. If a metric shows the same value across dashboards with the same filters, the system is working correctly.

### Related

- [Executive Overview](/documentation/executive-overview) — See all metrics in one view.
- [Forecast Module](/documentation/forecast-module) — Metrics-based revenue predictions.`,

      pt: `## Entendendo Métricas de Receita

O RevOps AI acompanha métricas-chave de receita usando fórmulas consistentes em toda a plataforma.

### Métricas Principais de Receita

#### Receita Fechada
- **Fórmula:** Soma dos valores dos deals marcados como Ganhos no período selecionado.
- **Uso:** Medida primária de performance de vendas.

#### Win Rate
- **Fórmula:** Deals Ganhos ÷ (Deals Ganhos + Deals Perdidos) × 100
- **Uso:** Mede a efetividade comercial. Não conta deals abertos.

#### Ticket Médio
- **Fórmula:** Receita Fechada Total ÷ Número de Deals Ganhos
- **Uso:** Indica qualidade da receita e composição dos deals.

#### Ciclo de Vendas
- **Fórmula:** Média de dias entre a data de criação e a data de fechamento dos deals ganhos.
- **Uso:** Mede a velocidade de vendas. Ciclos mais curtos geralmente indicam um processo mais saudável.

#### Cobertura de Pipeline
- **Fórmula:** Valor Total do Pipeline Aberto ÷ Meta de Receita
- **Uso:** Mostra quanto pipeline você tem em relação à sua meta. Uma proporção de 3x ou superior é tipicamente saudável.

#### Velocidade de Vendas
- **Fórmula:** (Número de Oportunidades × Win Rate × Ticket Médio) ÷ Duração do Ciclo de Vendas
- **Uso:** Quantifica a velocidade de geração de receita. Melhorar qualquer componente melhora a velocidade.

### Como Interpretar

- **Receita caindo?** → Verifique o win rate e o volume de deals.
- **Win rate declinando?** → Analise as razões de perda e a conversão por estágio.
- **Ciclos de venda longos?** → Procure estágios gargalo no funil.
- **Cobertura de pipeline baixa?** → Foque na geração e qualificação de leads.

### Garantia de Consistência de Dados

Cada métrica usa a mesma fórmula em todos os lugares onde aparece — na Visão Executiva, Relatórios, Forecast e Advisor de IA. Se uma métrica mostra o mesmo valor em diferentes dashboards com os mesmos filtros, o sistema está funcionando corretamente.

### Relacionados

- [Visão Executiva](/documentation/executive-overview) — Veja todas as métricas em uma visão.
- [Módulo de Forecast](/documentation/forecast-module) — Previsões de receita baseadas em métricas.`
    }
  },
  {
    slug: 'forecast-module',
    categoryKey: 'dashboards',
    title: { en: 'Using the forecast module', pt: 'Usando o módulo de forecast' },
    content: {
      en: `## Using the Forecast Module

The Forecast module provides mathematical revenue predictions based on your pipeline data and historical performance.

### How It Works

RevOps AI calculates forecasts using:

1. **Historical win rates** per pipeline stage.
2. **Current pipeline value** at each stage.
3. **Expected close dates** from your CRM.
4. **Stage-weighted probability** based on actual conversion data.

### Forecast Views

#### Mathematical Forecast
The system applies historical win rates to each deal based on its current stage, generating a weighted prediction of expected revenue.

#### Manager Forecast
Sales managers can input their own revenue estimates per rep and per month, adding a human judgment layer. The system then compares the mathematical forecast against the manager's forecast, highlighting discrepancies.

#### Comparison View
See side-by-side:
- Mathematical forecast (data-driven)
- Manager forecast (judgment-based)
- Actual target
- Current closed revenue

### Understanding the Numbers

| Metric | Meaning |
|--------|---------|
| **Weighted Pipeline** | Pipeline value × stage probability |
| **Best Case** | If all open deals close |
| **Most Likely** | Weighted by historical conversion |
| **Committed** | Only deals in final stages |

### Best Practices

- **Update manager forecasts weekly** to keep the comparison meaningful.
- **Watch for large gaps** between mathematical and manager forecasts — they indicate blind spots.
- **Use forecast accuracy over time** to calibrate both models.

### Related

- [Setting Targets](/documentation/setting-targets) — Set the goals your forecast measures against.
- [Sales Burndown](/documentation/burndown-coverage) — Track daily progress toward forecast.`,

      pt: `## Usando o Módulo de Forecast

O módulo de Forecast fornece previsões matemáticas de receita baseadas nos dados do seu pipeline e performance histórica.

### Como Funciona

O RevOps AI calcula forecasts usando:

1. **Win rates históricos** por estágio do pipeline.
2. **Valor atual do pipeline** em cada estágio.
3. **Datas de fechamento esperadas** do seu CRM.
4. **Probabilidade ponderada por estágio** baseada em dados reais de conversão.

### Visões de Forecast

#### Forecast Matemático
O sistema aplica win rates históricos a cada deal com base no estágio atual, gerando uma previsão ponderada da receita esperada.

#### Forecast do Gestor
Gestores comerciais podem inserir suas próprias estimativas de receita por vendedor e por mês, adicionando uma camada de julgamento humano. O sistema então compara o forecast matemático contra o forecast do gestor, destacando discrepâncias.

#### Visão Comparativa
Veja lado a lado:
- Forecast matemático (baseado em dados)
- Forecast do gestor (baseado em julgamento)
- Meta atual
- Receita já fechada

### Entendendo os Números

| Métrica | Significado |
|---------|-------------|
| **Pipeline Ponderado** | Valor do pipeline × probabilidade do estágio |
| **Melhor Cenário** | Se todos os deals abertos fecharem |
| **Mais Provável** | Ponderado pela conversão histórica |
| **Comprometido** | Apenas deals nos estágios finais |

### Melhores Práticas

- **Atualize o forecast do gestor semanalmente** para manter a comparação significativa.
- **Observe grandes gaps** entre o forecast matemático e o do gestor — eles indicam pontos cegos.
- **Use a precisão do forecast ao longo do tempo** para calibrar ambos os modelos.

### Relacionados

- [Configurando Metas](/documentation/setting-targets) — Defina as metas contra as quais seu forecast é medido.
- [Sales Burndown](/documentation/burndown-coverage) — Acompanhe o progresso diário em relação ao forecast.`
    }
  },
  {
    slug: 'burndown-coverage',
    categoryKey: 'dashboards',
    title: { en: 'Sales burndown and pipeline coverage', pt: 'Sales burndown e cobertura de pipeline' },
    content: {
      en: `## Sales Burndown & Pipeline Coverage

### Sales Burndown

The Sales Burndown chart tracks your daily progress toward the monthly revenue target, similar to how agile teams track sprint progress.

#### How to Read It

- **Target line** — A straight line from zero to your monthly target, showing the ideal daily pace.
- **Actual line** — Cumulative won revenue, updated daily.
- **Gap** — The distance between actual and target lines shows if you're ahead or behind.

#### Key Indicators

| Situation | What It Means | Action |
|-----------|--------------|--------|
| Actual above target line | Ahead of pace | Maintain momentum |
| Actual below target line | Behind pace | Accelerate closing activity |
| Flat actual line | No deals closing | Investigate pipeline health |

#### Alerts

You can configure alerts to notify you (via email or Slack) when:
- You fall behind pace by a configured threshold.
- You reach a certain percentage of the target.

### Pipeline Coverage

Pipeline Coverage measures how much open pipeline you have relative to your target.

#### Formula
**Pipeline Coverage = Open Pipeline Value ÷ Revenue Target**

#### Benchmarks

| Coverage | Interpretation |
|----------|---------------|
| < 2x | Insufficient pipeline — high risk of missing target |
| 2x–3x | Adequate coverage — depends on win rate |
| 3x–4x | Healthy coverage — standard benchmark |
| > 4x | Strong coverage — but watch for pipeline bloat |

> **Note:** The ideal coverage ratio depends on your win rate. Lower win rates require higher coverage.

### Sharing & Scheduling

Both the burndown and coverage views can be:
- **Shared via Slack** — Send automated reports to channels or DMs.
- **Scheduled** — Set daily or weekly automated reports.
- **Exported** — Download as PDF or image.

### Related

- [Setting Targets](/documentation/setting-targets) — Targets power the burndown calculation.
- [Executive Overview](/documentation/executive-overview) — See coverage in context with other KPIs.`,

      pt: `## Sales Burndown & Cobertura de Pipeline

### Sales Burndown

O gráfico de Sales Burndown acompanha seu progresso diário em direção à meta mensal de receita, similar à forma como equipes ágeis acompanham o progresso de sprints.

#### Como Ler

- **Linha de meta** — Uma linha reta de zero até sua meta mensal, mostrando o ritmo diário ideal.
- **Linha real** — Receita ganha acumulada, atualizada diariamente.
- **Gap** — A distância entre as linhas real e de meta mostra se você está à frente ou atrás.

#### Indicadores Chave

| Situação | O Que Significa | Ação |
|----------|----------------|------|
| Real acima da linha de meta | À frente do ritmo | Manter o momentum |
| Real abaixo da linha de meta | Atrás do ritmo | Acelerar atividade de fechamento |
| Linha real plana | Nenhum deal fechando | Investigar saúde do pipeline |

#### Alertas

Você pode configurar alertas para ser notificado (via e-mail ou Slack) quando:
- Você ficar atrás do ritmo por um limite configurado.
- Atingir um determinado percentual da meta.

### Cobertura de Pipeline

A Cobertura de Pipeline mede quanto pipeline aberto você tem em relação à sua meta.

#### Fórmula
**Cobertura de Pipeline = Valor do Pipeline Aberto ÷ Meta de Receita**

#### Benchmarks

| Cobertura | Interpretação |
|-----------|---------------|
| < 2x | Pipeline insuficiente — alto risco de não bater a meta |
| 2x–3x | Cobertura adequada — depende do win rate |
| 3x–4x | Cobertura saudável — benchmark padrão |
| > 4x | Cobertura forte — mas atenção com pipeline inchado |

> **Nota:** A proporção ideal de cobertura depende do seu win rate. Win rates mais baixos exigem cobertura maior.

### Compartilhamento & Agendamento

Tanto o burndown quanto a cobertura podem ser:
- **Compartilhados via Slack** — Envie relatórios automatizados para canais ou DMs.
- **Agendados** — Defina relatórios automáticos diários ou semanais.
- **Exportados** — Baixe como PDF ou imagem.

### Relacionados

- [Configurando Metas](/documentation/setting-targets) — Metas alimentam o cálculo do burndown.
- [Visão Executiva](/documentation/executive-overview) — Veja a cobertura no contexto com outros KPIs.`
    }
  },

  // ─── INTEGRATIONS ──────────────────────────────────────────
  {
    slug: 'hubspot-setup',
    categoryKey: 'integrations',
    title: { en: 'HubSpot integration setup', pt: 'Configuração da integração HubSpot' },
    content: {
      en: `## HubSpot Integration Setup

### Prerequisites

- A HubSpot account with **admin** or **super admin** permissions.
- An active RevOps AI organization.

### Step-by-Step Connection

1. Navigate to **Settings → Integrations** in RevOps AI.
2. Find the **HubSpot** card and click **Connect**.
3. You'll be redirected to HubSpot's authorization page.
4. Review the permissions being requested and click **Grant access**.
5. You'll be automatically redirected back to RevOps AI.
6. The connection status will show as **Connected** ✅.

### Data Synced

| Entity | Fields Imported |
|--------|----------------|
| **Deals** | Name, amount, stage, pipeline, owner, create date, close date, custom fields |
| **Contacts** | Name, email, phone, company, title, custom fields |
| **Companies** | Name, industry, size, revenue, website |
| **Activities** | Emails, calls, meetings, notes (linked to deals) |
| **Products** | Line items associated with deals |
| **Tasks** | Tasks with due dates, assignees, and status |

### Field Mapping

After connecting, RevOps AI automatically maps common HubSpot fields. You can customize this mapping in **Settings → Integrations → Field Mapping**.

Key auto-mapped fields:
- \`dealname\` → Deal name
- \`amount\` → Deal value
- \`dealstage\` → Stage
- \`pipeline\` → Pipeline
- \`closedate\` → Closed at date
- \`hubspot_owner_id\` → Deal owner

> See [Custom Field Mapping](/documentation/field-mapping) for advanced configuration.

### Sync Behavior

- **Initial sync** — Imports all historical data. May take 10–30 minutes depending on data volume.
- **Daily sync** — Runs automatically once per day at the configured hour.
- **Manual sync** — Can be triggered from **Settings → Integrations**.
- **Incremental** — Only syncs records that changed since the last sync.

### Troubleshooting

- **Authorization failed?** → Ensure you have admin access to HubSpot.
- **Missing deals?** → Check if your HubSpot user has access to all pipelines.
- **Sync errors?** → See [Sync Troubleshooting](/documentation/sync-troubleshooting).

### Related

- [Custom Field Mapping](/documentation/field-mapping) — Map additional HubSpot fields.
- [Connecting your CRM](/documentation/connecting-crm) — General CRM connection guide.`,

      pt: `## Configuração da Integração HubSpot

### Pré-requisitos

- Uma conta HubSpot com permissões de **admin** ou **super admin**.
- Uma organização RevOps AI ativa.

### Conexão Passo a Passo

1. Navegue até **Configurações → Integrações** no RevOps AI.
2. Encontre o card do **HubSpot** e clique em **Conectar**.
3. Você será redirecionado para a página de autorização do HubSpot.
4. Revise as permissões solicitadas e clique em **Conceder acesso**.
5. Você será automaticamente redirecionado de volta ao RevOps AI.
6. O status da conexão mostrará **Conectado** ✅.

### Dados Sincronizados

| Entidade | Campos Importados |
|----------|-------------------|
| **Deals** | Nome, valor, estágio, pipeline, responsável, data de criação, data de fechamento, campos customizados |
| **Contatos** | Nome, e-mail, telefone, empresa, cargo, campos customizados |
| **Empresas** | Nome, indústria, porte, receita, website |
| **Atividades** | E-mails, ligações, reuniões, notas (vinculadas aos deals) |
| **Produtos** | Itens de linha associados aos deals |
| **Tarefas** | Tarefas com datas de vencimento, responsáveis e status |

### Mapeamento de Campos

Após conectar, o RevOps AI mapeia automaticamente os campos comuns do HubSpot. Você pode personalizar esse mapeamento em **Configurações → Integrações → Mapeamento de Campos**.

Campos mapeados automaticamente:
- \`dealname\` → Nome do deal
- \`amount\` → Valor do deal
- \`dealstage\` → Estágio
- \`pipeline\` → Pipeline
- \`closedate\` → Data de fechamento
- \`hubspot_owner_id\` → Responsável pelo deal

> Veja [Mapeamento de Campos Customizados](/documentation/field-mapping) para configuração avançada.

### Comportamento da Sincronização

- **Sincronização inicial** — Importa todo o histórico de dados. Pode levar de 10 a 30 minutos dependendo do volume.
- **Sincronização diária** — Executa automaticamente uma vez por dia no horário configurado.
- **Sincronização manual** — Pode ser disparada em **Configurações → Integrações**.
- **Incremental** — Sincroniza apenas registros alterados desde a última sincronização.

### Resolução de Problemas

- **Autorização falhou?** → Verifique se você tem acesso admin no HubSpot.
- **Deals faltando?** → Confira se seu usuário HubSpot tem acesso a todos os pipelines.
- **Erros de sincronização?** → Veja [Resolução de Problemas de Sync](/documentation/sync-troubleshooting).

### Relacionados

- [Mapeamento de Campos Customizados](/documentation/field-mapping) — Mapeie campos adicionais do HubSpot.
- [Conectando seu CRM](/documentation/connecting-crm) — Guia geral de conexão de CRM.`
    }
  },
  {
    slug: 'rdstation-setup',
    categoryKey: 'integrations',
    title: { en: 'RD Station CRM setup', pt: 'Configuração do RD Station CRM' },
    content: {
      en: `## RD Station CRM Setup

### Prerequisites

- An active RD Station CRM account.
- Admin access to generate an API token.

### Step-by-Step Connection

1. **Get your API Token from RD Station:**
   - Log in to RD Station CRM.
   - Go to **Settings → Integrations → API Token**.
   - Copy the token.

2. **Connect in RevOps AI:**
   - Navigate to **Settings → Integrations**.
   - Click **Connect** on the RD Station CRM card.
   - Paste your API Token.
   - Click **Save and Connect**.

3. **Verify the connection:**
   - The status should show **Connected** ✅.
   - The initial sync will begin automatically.

### Data Synced

| Entity | Details |
|--------|---------|
| **Deals** | Name, value, stage, pipeline, owner, creation date, status |
| **Contacts** | Name, email, phone, company, position |
| **Organizations** | Company data linked to contacts |
| **Activities** | Interaction history on deals |
| **Tasks** | Pending and completed tasks |
| **Deal Notes** | Notes added to deals by reps |

### Field Mapping

RD Station CRM fields are automatically mapped to RevOps AI's standard fields. Key mappings:

- \`name\` → Deal name
- \`amount\` → Deal value
- \`deal_stage\` → Stage
- \`deal_pipeline\` → Pipeline
- \`expected_close_date\` → Close date
- \`user\` → Deal owner

### Sync Details

- **Initial sync:** Imports complete historical data.
- **Daily sync:** Automatic daily synchronization.
- **Manual sync:** Available via Settings → Integrations.

### Troubleshooting

- **Token invalid?** → Generate a new token in RD Station CRM settings.
- **Partial data?** → Ensure the token has access to all pipelines.
- **Sync stuck?** → See [Sync Troubleshooting](/documentation/sync-troubleshooting).

### Related

- [Custom Field Mapping](/documentation/field-mapping)
- [Connecting your CRM](/documentation/connecting-crm)`,

      pt: `## Configuração do RD Station CRM

### Pré-requisitos

- Uma conta RD Station CRM ativa.
- Acesso de administrador para gerar um token de API.

### Conexão Passo a Passo

1. **Obtenha seu Token de API no RD Station:**
   - Faça login no RD Station CRM.
   - Vá em **Configurações → Integrações → Token de API**.
   - Copie o token.

2. **Conecte no RevOps AI:**
   - Navegue até **Configurações → Integrações**.
   - Clique em **Conectar** no card do RD Station CRM.
   - Cole seu Token de API.
   - Clique em **Salvar e Conectar**.

3. **Verifique a conexão:**
   - O status deve mostrar **Conectado** ✅.
   - A sincronização inicial começará automaticamente.

### Dados Sincronizados

| Entidade | Detalhes |
|----------|----------|
| **Deals** | Nome, valor, estágio, pipeline, responsável, data de criação, status |
| **Contatos** | Nome, e-mail, telefone, empresa, cargo |
| **Organizações** | Dados da empresa vinculados aos contatos |
| **Atividades** | Histórico de interações nos deals |
| **Tarefas** | Tarefas pendentes e concluídas |
| **Notas de Deal** | Notas adicionadas aos deals pelos vendedores |

### Mapeamento de Campos

Os campos do RD Station CRM são automaticamente mapeados para os campos padrão do RevOps AI. Mapeamentos chave:

- \`name\` → Nome do deal
- \`amount\` → Valor do deal
- \`deal_stage\` → Estágio
- \`deal_pipeline\` → Pipeline
- \`expected_close_date\` → Data de fechamento
- \`user\` → Responsável pelo deal

### Detalhes da Sincronização

- **Sincronização inicial:** Importa o histórico completo de dados.
- **Sincronização diária:** Sincronização automática diária.
- **Sincronização manual:** Disponível via Configurações → Integrações.

### Resolução de Problemas

- **Token inválido?** → Gere um novo token nas configurações do RD Station CRM.
- **Dados parciais?** → Verifique se o token tem acesso a todos os pipelines.
- **Sync travado?** → Veja [Resolução de Problemas de Sync](/documentation/sync-troubleshooting).

### Relacionados

- [Mapeamento de Campos Customizados](/documentation/field-mapping)
- [Conectando seu CRM](/documentation/connecting-crm)`
    }
  },
  {
    slug: 'field-mapping',
    categoryKey: 'integrations',
    title: { en: 'Custom field mapping', pt: 'Mapeamento de campos customizados' },
    content: {
      en: `## Custom Field Mapping

Field mapping connects your CRM fields to RevOps AI's internal data model, ensuring accurate analytics.

### How It Works

When you connect your CRM, RevOps AI automatically maps standard fields (name, amount, stage, etc.). For additional or custom fields, you can configure manual mappings.

### Accessing Field Mapping

1. Go to **Settings → Integrations**.
2. Click on your connected CRM.
3. Select **Field Mapping**.

### Mapping Interface

The mapping interface shows:

| Column | Description |
|--------|-------------|
| **CRM Field** | The field name as it exists in your CRM |
| **System Field** | The RevOps AI field it maps to |
| **Entity** | The entity type (Deal, Contact, Company) |
| **Status** | Active or Inactive |

### Available System Fields for Deals

| System Field | Description | Used For |
|--------------|-------------|----------|
| \`name\` | Deal name | Display |
| \`amount\` | Deal monetary value | Revenue calculations |
| \`stage\` | Current pipeline stage | Funnel analytics |
| \`pipeline\` | Pipeline identifier | Pipeline filtering |
| \`close_date\` | Expected close date | Forecast, scheduling |
| \`closed_at\` | Actual close date (won/lost) | Revenue period attribution |
| \`deal_source\` | Lead/deal origin | Source analytics |
| \`lost_reason\` | Reason for losing | Loss analysis |
| \`owner_email\` | Sales rep email | Rep performance |
| \`probability\` | Win probability | Weighted pipeline |
| \`mrr\` | Monthly recurring revenue | MRR tracking |
| \`campaign\` | Marketing campaign | Campaign ROI |

### Auto-Mapping Rules

RevOps AI auto-suggests mappings based on field names:

**HubSpot:**
- \`dealname\` → \`name\`
- \`amount\` → \`amount\`
- \`dealstage\` → \`stage\`
- \`closedate\` → \`closed_at\`
- \`hs_deal_stage_probability\` → \`probability\`

**RD Station:**
- \`name\` → \`name\`
- \`amount\` → \`amount\`
- \`deal_stage\` → \`stage\`
- \`expected_close_date\` → \`close_date\`

### Best Practices

- **Map all revenue-related fields** — amount, MRR, products.
- **Map owner fields** — Essential for rep performance analytics.
- **Map dates accurately** — \`close_date\` (expected) vs \`closed_at\` (actual) serve different purposes.
- **Review after CRM changes** — When you add custom fields in your CRM, update the mapping.

### Related

- [HubSpot Setup](/documentation/hubspot-setup) — HubSpot-specific configuration.
- [RD Station Setup](/documentation/rdstation-setup) — RD Station-specific configuration.
- [Sync Troubleshooting](/documentation/sync-troubleshooting) — Fix data sync issues.`,

      pt: `## Mapeamento de Campos Customizados

O mapeamento de campos conecta os campos do seu CRM ao modelo de dados interno do RevOps AI, garantindo análises precisas.

### Como Funciona

Quando você conecta seu CRM, o RevOps AI mapeia automaticamente campos padrão (nome, valor, estágio, etc.). Para campos adicionais ou customizados, você pode configurar mapeamentos manuais.

### Acessando o Mapeamento de Campos

1. Acesse **Configurações → Integrações**.
2. Clique no CRM conectado.
3. Selecione **Mapeamento de Campos**.

### Interface de Mapeamento

A interface de mapeamento mostra:

| Coluna | Descrição |
|--------|-----------|
| **Campo do CRM** | O nome do campo como existe no seu CRM |
| **Campo do Sistema** | O campo do RevOps AI ao qual ele mapeia |
| **Entidade** | O tipo de entidade (Deal, Contato, Empresa) |
| **Status** | Ativo ou Inativo |

### Campos Disponíveis para Deals

| Campo do Sistema | Descrição | Usado Para |
|-----------------|-----------|-----------|
| \`name\` | Nome do deal | Exibição |
| \`amount\` | Valor monetário do deal | Cálculos de receita |
| \`stage\` | Estágio atual do pipeline | Análise de funil |
| \`pipeline\` | Identificador do pipeline | Filtragem por pipeline |
| \`close_date\` | Data de fechamento esperada | Forecast, agendamento |
| \`closed_at\` | Data real de fechamento (ganho/perdido) | Atribuição de receita por período |
| \`deal_source\` | Origem do lead/deal | Analytics de origem |
| \`lost_reason\` | Motivo de perda | Análise de perdas |
| \`owner_email\` | E-mail do vendedor | Performance por vendedor |
| \`probability\` | Probabilidade de ganho | Pipeline ponderado |
| \`mrr\` | Receita mensal recorrente | Acompanhamento de MRR |
| \`campaign\` | Campanha de marketing | ROI de campanha |

### Regras de Auto-Mapeamento

O RevOps AI sugere mapeamentos automaticamente com base nos nomes dos campos:

**HubSpot:**
- \`dealname\` → \`name\`
- \`amount\` → \`amount\`
- \`dealstage\` → \`stage\`
- \`closedate\` → \`closed_at\`
- \`hs_deal_stage_probability\` → \`probability\`

**RD Station:**
- \`name\` → \`name\`
- \`amount\` → \`amount\`
- \`deal_stage\` → \`stage\`
- \`expected_close_date\` → \`close_date\`

### Melhores Práticas

- **Mapeie todos os campos de receita** — valor, MRR, produtos.
- **Mapeie campos de responsável** — Essencial para analytics de performance por vendedor.
- **Mapeie datas com precisão** — \`close_date\` (esperada) vs \`closed_at\` (real) servem propósitos diferentes.
- **Revise após mudanças no CRM** — Quando adicionar campos customizados no CRM, atualize o mapeamento.

### Relacionados

- [Configuração HubSpot](/documentation/hubspot-setup) — Configuração específica do HubSpot.
- [Configuração RD Station](/documentation/rdstation-setup) — Configuração específica do RD Station.
- [Resolução de Problemas de Sync](/documentation/sync-troubleshooting) — Corrija problemas de sincronização.`
    }
  },
  {
    slug: 'sync-troubleshooting',
    categoryKey: 'integrations',
    title: { en: 'Sync troubleshooting', pt: 'Resolução de problemas de sync' },
    content: {
      en: `## Sync Troubleshooting

If your CRM data isn't syncing correctly, follow these steps to diagnose and resolve the issue.

### Common Issues

#### 1. Sync Shows "Failed" Status

**Possible causes:**
- CRM API token expired or was revoked.
- HubSpot OAuth authorization expired.
- CRM API rate limits exceeded.

**Resolution:**
- Go to **Settings → Integrations**.
- Click **Reconnect** on your CRM card.
- For HubSpot: Re-authorize via OAuth.
- For RD Station: Generate and enter a new API token.

#### 2. Missing Deals or Records

**Possible causes:**
- The CRM user doesn't have access to all pipelines.
- Deals were created after the last sync.
- Data volume exceeds sync batch limits.

**Resolution:**
- Verify your CRM user has access to all pipelines and records.
- Trigger a manual sync from **Settings → Integrations**.
- Wait for the background sync to process all batches.

#### 3. Incorrect Field Values

**Possible causes:**
- Field mapping is misconfigured.
- CRM field type mismatch (e.g., text field mapped to a numeric field).

**Resolution:**
- Review field mappings in **Settings → Integrations → Field Mapping**.
- Ensure CRM fields contain the expected data types.
- See [Custom Field Mapping](/documentation/field-mapping) for details.

#### 4. Sync Takes Too Long

**What to expect:**
- Initial sync: 5–30 minutes depending on data volume.
- Daily sync: Usually 2–10 minutes.

**If it's taking longer:**
- Large CRM accounts (>10,000 deals) may take longer.
- Check the sync progress indicator in **Settings → Integrations**.
- The sync processes records in batches — this is normal behavior.

#### 5. Duplicate Records

**Possible cause:** External IDs changed in the CRM.

**Resolution:** This is rare. Contact support if you notice duplicates.

### Checking Sync Status

In **Settings → Integrations**, you can see:
- **Last sync timestamp** — When the last sync completed.
- **Records synced** — Number of records processed.
- **Sync status** — Running, Completed, or Failed.
- **Error details** — If failed, the error message is shown.

### Forcing a Full Re-sync

If data seems inconsistent:
1. Go to **Settings → Integrations**.
2. Click on your CRM connection.
3. Select **Trigger Sync** to force a full re-sync.

> **Note:** A full re-sync may take longer than incremental syncs.

### Related

- [HubSpot Setup](/documentation/hubspot-setup)
- [RD Station Setup](/documentation/rdstation-setup)
- [Field Mapping](/documentation/field-mapping)`,

      pt: `## Resolução de Problemas de Sync

Se os dados do seu CRM não estão sincronizando corretamente, siga estes passos para diagnosticar e resolver o problema.

### Problemas Comuns

#### 1. Sincronização com Status "Falhou"

**Possíveis causas:**
- Token de API do CRM expirou ou foi revogado.
- Autorização OAuth do HubSpot expirou.
- Limites de taxa da API do CRM excedidos.

**Resolução:**
- Acesse **Configurações → Integrações**.
- Clique em **Reconectar** no card do seu CRM.
- Para HubSpot: Re-autorize via OAuth.
- Para RD Station: Gere e insira um novo token de API.

#### 2. Deals ou Registros Faltando

**Possíveis causas:**
- O usuário do CRM não tem acesso a todos os pipelines.
- Os deals foram criados após a última sincronização.
- O volume de dados excede os limites de lote da sincronização.

**Resolução:**
- Verifique se seu usuário do CRM tem acesso a todos os pipelines e registros.
- Dispare uma sincronização manual em **Configurações → Integrações**.
- Aguarde a sincronização em segundo plano processar todos os lotes.

#### 3. Valores de Campos Incorretos

**Possíveis causas:**
- O mapeamento de campos está mal configurado.
- Incompatibilidade de tipo de campo (ex: campo texto mapeado para campo numérico).

**Resolução:**
- Revise os mapeamentos de campo em **Configurações → Integrações → Mapeamento de Campos**.
- Garanta que os campos do CRM contêm os tipos de dados esperados.
- Veja [Mapeamento de Campos Customizados](/documentation/field-mapping) para detalhes.

#### 4. Sincronização Demora Muito

**O que esperar:**
- Sincronização inicial: 5–30 minutos dependendo do volume de dados.
- Sincronização diária: Geralmente 2–10 minutos.

**Se está demorando mais:**
- Contas CRM grandes (>10.000 deals) podem demorar mais.
- Verifique o indicador de progresso em **Configurações → Integrações**.
- A sincronização processa registros em lotes — isso é comportamento normal.

#### 5. Registros Duplicados

**Possível causa:** IDs externos mudaram no CRM.

**Resolução:** Isso é raro. Contate o suporte se notar duplicatas.

### Verificando o Status da Sincronização

Em **Configurações → Integrações**, você pode ver:
- **Timestamp da última sincronização** — Quando a última sync foi concluída.
- **Registros sincronizados** — Número de registros processados.
- **Status da sincronização** — Em andamento, Concluída ou Falha.
- **Detalhes do erro** — Se falhou, a mensagem de erro é exibida.

### Forçando uma Re-sincronização Completa

Se os dados parecem inconsistentes:
1. Acesse **Configurações → Integrações**.
2. Clique na conexão do seu CRM.
3. Selecione **Disparar Sincronização** para forçar uma re-sincronização completa.

> **Nota:** Uma re-sincronização completa pode demorar mais que sincronizações incrementais.

### Relacionados

- [Configuração HubSpot](/documentation/hubspot-setup)
- [Configuração RD Station](/documentation/rdstation-setup)
- [Mapeamento de Campos](/documentation/field-mapping)`
    }
  },

  // ─── SETTINGS & ADMIN ─────────────────────────────────────
  {
    slug: 'user-management',
    categoryKey: 'settings',
    title: { en: 'User management and roles', pt: 'Gerenciamento de usuários e permissões' },
    content: {
      en: `## User Management

Manage who has access to your RevOps AI organization and what they can see and do.

### Inviting Users

1. Go to **Settings → Users**.
2. Click **Invite User**.
3. Enter the person's email address.
4. Select a **permission profile** (controls access levels).
5. Click **Send Invitation**.

The invited user receives an email with a link to create their account and join your organization.

### User Roles

RevOps AI uses a role-based access system:

| Role | Description |
|------|-------------|
| **Admin** | Full access to all features, settings, and user management |
| **Member** | Access controlled by permission profiles |

### Permission Profiles

Instead of fixed roles, RevOps AI uses customizable permission profiles. Each profile defines exactly which features a user can access.

See [Permission Profiles](/documentation/permission-profiles) for detailed configuration.

### Managing Existing Users

In **Settings → Users**, you can:

- **View all members** — See everyone in your organization.
- **Change permission profiles** — Update a user's access level.
- **Remove users** — Revoke access (the user's account is not deleted, just disassociated from your org).
- **Resend invitations** — For pending invitations that may have expired.

### Best Practices

- **Apply least privilege** — Give users only the access they need.
- **Use permission profiles** — Instead of making everyone an admin.
- **Audit regularly** — Review user list when team members change roles or leave.

### Related

- [Permission Profiles](/documentation/permission-profiles) — Configure custom access levels.
- [Creating your account](/documentation/creating-account) — Account setup guide.`,

      pt: `## Gerenciamento de Usuários

Gerencie quem tem acesso à sua organização no RevOps AI e o que podem ver e fazer.

### Convidando Usuários

1. Acesse **Configurações → Usuários**.
2. Clique em **Convidar Usuário**.
3. Insira o endereço de e-mail da pessoa.
4. Selecione um **perfil de permissão** (controla os níveis de acesso).
5. Clique em **Enviar Convite**.

O usuário convidado recebe um e-mail com um link para criar sua conta e entrar na sua organização.

### Papéis de Usuário

O RevOps AI usa um sistema de acesso baseado em papéis:

| Papel | Descrição |
|-------|-----------|
| **Admin** | Acesso total a todas as funcionalidades, configurações e gestão de usuários |
| **Membro** | Acesso controlado por perfis de permissão |

### Perfis de Permissão

Em vez de papéis fixos, o RevOps AI usa perfis de permissão customizáveis. Cada perfil define exatamente quais funcionalidades um usuário pode acessar.

Veja [Perfis de Permissão](/documentation/permission-profiles) para configuração detalhada.

### Gerenciando Usuários Existentes

Em **Configurações → Usuários**, você pode:

- **Ver todos os membros** — Veja todos na sua organização.
- **Alterar perfis de permissão** — Atualize o nível de acesso de um usuário.
- **Remover usuários** — Revogue o acesso (a conta do usuário não é deletada, apenas desassociada da sua org).
- **Reenviar convites** — Para convites pendentes que possam ter expirado.

### Melhores Práticas

- **Aplique o menor privilégio** — Dê aos usuários apenas o acesso que precisam.
- **Use perfis de permissão** — Em vez de tornar todos administradores.
- **Audite regularmente** — Revise a lista de usuários quando membros mudam de função ou saem.

### Relacionados

- [Perfis de Permissão](/documentation/permission-profiles) — Configure níveis de acesso customizados.
- [Criando sua conta](/documentation/creating-account) — Guia de configuração de conta.`
    }
  },
  {
    slug: 'permission-profiles',
    categoryKey: 'settings',
    title: { en: 'Permission profiles', pt: 'Perfis de permissão' },
    content: {
      en: `## Permission Profiles

Permission profiles let you create custom access levels for your team members, controlling exactly which features and dashboards each person can use.

### Default Profiles

RevOps AI comes with pre-configured profiles:

| Profile | Typical Use | Access Level |
|---------|-------------|--------------|
| **Full Access** | Team leads, managers | All features enabled |
| **Analyst** | Data analysts, ops | Dashboards and reports, no settings |
| **Sales Rep** | Individual contributors | Limited to personal performance and deals |

### Creating a Custom Profile

1. Go to **Settings → Permissions**.
2. Click **Create Profile**.
3. Name the profile (e.g., "Regional Manager").
4. Toggle each permission on or off.
5. Click **Save**.

### Available Permissions

Permissions are organized by feature area:

| Permission | Controls Access To |
|------------|--------------------|
| Executive Overview | Main executive dashboard |
| Revenue | Revenue analytics page |
| Funnel | Funnel analysis and conversion |
| Pipeline Coverage | Pipeline coverage metrics |
| Burndown | Sales burndown tracking |
| Forecast | Forecast module |
| Analytics | Sales analytics and deep dives |
| Reports | Report generation and export |
| Deals | Deal list and details |
| Leads | Lead management |
| Targets | Target setting and tracking |
| Settings | Organization settings |
| Integrations | CRM connection management |
| User Management | Invite and manage users |
| Billing | Subscription and billing |
| AI Advisor | AI-powered recommendations |
| Data Export | Export functionality |

### Assigning Profiles

When inviting a user or editing an existing member:
1. Go to **Settings → Users**.
2. Click the user's permission profile dropdown.
3. Select the desired profile.

### Best Practices

- **Start with default profiles** — Customize only when needed.
- **Create role-specific profiles** — E.g., "VP Sales" with executive dashboards only.
- **Don't over-restrict** — Users need enough access to do their jobs effectively.

### Related

- [User Management](/documentation/user-management) — Manage team access.`,

      pt: `## Perfis de Permissão

Os perfis de permissão permitem criar níveis de acesso customizados para os membros da sua equipe, controlando exatamente quais funcionalidades e dashboards cada pessoa pode usar.

### Perfis Padrão

O RevOps AI vem com perfis pré-configurados:

| Perfil | Uso Típico | Nível de Acesso |
|--------|------------|-----------------|
| **Acesso Total** | Líderes, gestores | Todas as funcionalidades habilitadas |
| **Analista** | Analistas de dados, ops | Dashboards e relatórios, sem configurações |
| **Vendedor** | Contribuidores individuais | Limitado a performance pessoal e deals |

### Criando um Perfil Customizado

1. Acesse **Configurações → Permissões**.
2. Clique em **Criar Perfil**.
3. Nomeie o perfil (ex: "Gerente Regional").
4. Ative ou desative cada permissão.
5. Clique em **Salvar**.

### Permissões Disponíveis

As permissões são organizadas por área de funcionalidade:

| Permissão | Controla Acesso A |
|-----------|-------------------|
| Visão Executiva | Dashboard executivo principal |
| Receita | Página de analytics de receita |
| Funil | Análise de funil e conversão |
| Cobertura de Pipeline | Métricas de cobertura de pipeline |
| Burndown | Acompanhamento de sales burndown |
| Forecast | Módulo de forecast |
| Analytics | Sales analytics e análises aprofundadas |
| Relatórios | Geração e exportação de relatórios |
| Deals | Lista e detalhes de deals |
| Leads | Gestão de leads |
| Metas | Definição e acompanhamento de metas |
| Configurações | Configurações da organização |
| Integrações | Gestão de conexão com CRM |
| Gestão de Usuários | Convidar e gerenciar usuários |
| Faturamento | Assinatura e faturamento |
| Advisor IA | Recomendações com inteligência artificial |
| Exportação de Dados | Funcionalidade de exportação |

### Atribuindo Perfis

Ao convidar um usuário ou editar um membro existente:
1. Acesse **Configurações → Usuários**.
2. Clique no dropdown de perfil de permissão do usuário.
3. Selecione o perfil desejado.

### Melhores Práticas

- **Comece com perfis padrão** — Customize apenas quando necessário.
- **Crie perfis específicos por papel** — Ex: "VP Vendas" somente com dashboards executivos.
- **Não restrinja demais** — Usuários precisam de acesso suficiente para fazer seu trabalho de forma efetiva.

### Relacionados

- [Gerenciamento de Usuários](/documentation/user-management) — Gerencie o acesso da equipe.`
    }
  },
  {
    slug: 'billing',
    categoryKey: 'settings',
    title: { en: 'Billing and subscription', pt: 'Faturamento e assinatura' },
    content: {
      en: `## Billing and Subscription

RevOps AI offers flexible plans to fit teams of all sizes.

### Plans

| Plan | Best For | Key Features |
|------|----------|--------------|
| **Starter** | Small teams getting started | Core dashboards, 1 CRM connection, basic analytics |
| **Growth** | Growing sales teams | All dashboards, AI insights, forecast, team targets |
| **Pro** | Enterprise RevOps teams | Everything + advanced analytics, playbooks, priority support |

### Managing Your Subscription

1. Go to **Settings → Billing**.
2. View your current plan and usage.
3. Upgrade, downgrade, or manage payment methods.

### Billing Cycle

- Subscriptions are billed **monthly**.
- Payments are processed through **Stripe** (secure payment processing).
- Invoices are available for download in the billing page.

### Currency

Plans are available in **USD** and **BRL** (Brazilian Real). The currency is set at subscription time.

### Free Trial

New organizations get a **7-day free trial** with full access to all features. No credit card required to start.

After the trial:
- Choose a plan to continue using the platform.
- Your data is preserved even if the trial expires.

### Changing Plans

- **Upgrade** — Takes effect immediately. You're charged the prorated difference.
- **Downgrade** — Takes effect at the end of the current billing period.
- **Cancel** — Access continues until the end of the current billing period.

### Payment Methods

Accepted payment methods:
- Credit card (Visa, Mastercard, Amex)
- Debit card

### Invoices

Download invoices from **Settings → Billing → Invoice History**.

### Related

- [Creating your account](/documentation/creating-account) — Getting started.
- [User Management](/documentation/user-management) — Managing team access.`,

      pt: `## Faturamento e Assinatura

O RevOps AI oferece planos flexíveis para equipes de todos os tamanhos.

### Planos

| Plano | Ideal Para | Funcionalidades Principais |
|-------|-----------|---------------------------|
| **Starter** | Equipes pequenas começando | Dashboards principais, 1 conexão de CRM, analytics básicos |
| **Growth** | Equipes comerciais em crescimento | Todos os dashboards, insights de IA, forecast, metas por equipe |
| **Pro** | Equipes de RevOps enterprise | Tudo + analytics avançados, playbooks, suporte prioritário |

### Gerenciando Sua Assinatura

1. Acesse **Configurações → Faturamento**.
2. Veja seu plano atual e uso.
3. Faça upgrade, downgrade ou gerencie métodos de pagamento.

### Ciclo de Faturamento

- As assinaturas são cobradas **mensalmente**.
- Os pagamentos são processados pelo **Stripe** (processamento seguro de pagamentos).
- As faturas estão disponíveis para download na página de faturamento.

### Moeda

Os planos estão disponíveis em **USD** e **BRL** (Real Brasileiro). A moeda é definida no momento da assinatura.

### Período de Teste

Novas organizações ganham um **teste gratuito de 7 dias** com acesso total a todas as funcionalidades. Não é necessário cartão de crédito para começar.

Após o período de teste:
- Escolha um plano para continuar usando a plataforma.
- Seus dados são preservados mesmo se o teste expirar.

### Alterando Planos

- **Upgrade** — Efeito imediato. Você é cobrado pela diferença proporcional.
- **Downgrade** — Efeito no final do período de faturamento atual.
- **Cancelamento** — O acesso continua até o final do período de faturamento atual.

### Métodos de Pagamento

Métodos de pagamento aceitos:
- Cartão de crédito (Visa, Mastercard, Amex)
- Cartão de débito

### Faturas

Baixe faturas em **Configurações → Faturamento → Histórico de Faturas**.

### Relacionados

- [Criando sua conta](/documentation/creating-account) — Primeiros passos.
- [Gerenciamento de Usuários](/documentation/user-management) — Gerenciando acesso da equipe.`
    }
  },
  {
    slug: 'data-export',
    categoryKey: 'settings',
    title: { en: 'Data export options', pt: 'Opções de exportação de dados' },
    content: {
      en: `## Data Export Options

RevOps AI provides multiple ways to export your data for external analysis, reporting, or archival.

### Export from Dashboards

Most dashboards include an export option:

1. Navigate to any dashboard or analytics page.
2. Look for the **Export** or **Download** button (usually in the top right).
3. Choose your format:
   - **PDF** — Formatted report with charts and insights.
   - **Image** — Screenshot of the current view.

### Data Export Page

For bulk data exports:

1. Go to **Data Export** in the main navigation.
2. Select the data type you want to export:
   - Deals
   - Contacts
   - Activities
   - Pipeline metrics
3. Apply filters (date range, pipeline, stage, etc.).
4. Choose the export format:
   - **CSV** — For spreadsheets and data analysis.
   - **Excel (.xlsx)** — Formatted spreadsheet with headers.
5. Click **Export**.

### Report Exports

Generated reports can be exported as:
- **PDF reports** — Complete formatted documents.
- **Shared via Slack** — Automated report delivery.

### What Data Can Be Exported?

| Data Type | Available Formats | Includes |
|-----------|-------------------|----------|
| Deals | CSV, Excel | All mapped fields, amounts, stages, owners |
| Contacts | CSV, Excel | Names, emails, companies, custom fields |
| Activities | CSV, Excel | Types, dates, associated deals |
| Dashboards | PDF, Image | Charts, KPIs, insights |
| Reports | PDF | Full formatted reports |

### Scheduled Exports

For recurring export needs:
- Use the **Burndown sharing** feature to schedule automated Slack reports.
- Reports can be scheduled for daily or weekly delivery.

### Data Retention

Your synced CRM data is retained according to your organization's data retention settings, configurable in **Settings → General**.

### Related

- [Executive Overview](/documentation/executive-overview) — Export dashboard views.
- [Billing](/documentation/billing) — Export features availability by plan.`,

      pt: `## Opções de Exportação de Dados

O RevOps AI oferece múltiplas formas de exportar seus dados para análise externa, relatórios ou arquivamento.

### Exportação a Partir de Dashboards

A maioria dos dashboards inclui uma opção de exportação:

1. Navegue até qualquer dashboard ou página de analytics.
2. Procure o botão **Exportar** ou **Download** (geralmente no canto superior direito).
3. Escolha o formato:
   - **PDF** — Relatório formatado com gráficos e insights.
   - **Imagem** — Screenshot da visualização atual.

### Página de Exportação de Dados

Para exportações em massa:

1. Acesse **Exportação de Dados** na navegação principal.
2. Selecione o tipo de dado que deseja exportar:
   - Deals
   - Contatos
   - Atividades
   - Métricas de pipeline
3. Aplique filtros (intervalo de datas, pipeline, estágio, etc.).
4. Escolha o formato de exportação:
   - **CSV** — Para planilhas e análise de dados.
   - **Excel (.xlsx)** — Planilha formatada com cabeçalhos.
5. Clique em **Exportar**.

### Exportação de Relatórios

Relatórios gerados podem ser exportados como:
- **Relatórios PDF** — Documentos completos e formatados.
- **Compartilhados via Slack** — Entrega automatizada de relatórios.

### Quais Dados Podem Ser Exportados?

| Tipo de Dado | Formatos Disponíveis | Inclui |
|--------------|---------------------|--------|
| Deals | CSV, Excel | Todos os campos mapeados, valores, estágios, responsáveis |
| Contatos | CSV, Excel | Nomes, e-mails, empresas, campos customizados |
| Atividades | CSV, Excel | Tipos, datas, deals associados |
| Dashboards | PDF, Imagem | Gráficos, KPIs, insights |
| Relatórios | PDF | Relatórios completos formatados |

### Exportações Agendadas

Para necessidades recorrentes de exportação:
- Use a funcionalidade de **compartilhamento do Burndown** para agendar relatórios automatizados via Slack.
- Relatórios podem ser agendados para entrega diária ou semanal.

### Retenção de Dados

Seus dados sincronizados do CRM são mantidos de acordo com as configurações de retenção de dados da sua organização, configuráveis em **Configurações → Geral**.

### Relacionados

- [Visão Executiva](/documentation/executive-overview) — Exporte visualizações de dashboard.
- [Faturamento](/documentation/billing) — Disponibilidade de funcionalidades de exportação por plano.`
    }
  },
];

export function getArticleBySlug(slug: string): DocArticle | undefined {
  return documentationArticles.find(a => a.slug === slug);
}

export function getArticlesByCategory(categoryKey: string): DocArticle[] {
  return documentationArticles.filter(a => a.categoryKey === categoryKey);
}

export function getAdjacentArticles(slug: string): { prev: DocArticle | null; next: DocArticle | null } {
  const index = documentationArticles.findIndex(a => a.slug === slug);
  return {
    prev: index > 0 ? documentationArticles[index - 1] : null,
    next: index < documentationArticles.length - 1 ? documentationArticles[index + 1] : null,
  };
}
